import { zodResolver } from "@hookform/resolvers/zod";
import type { MouseEvent } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

import { UIButton } from "common/components";
import { MultiSelect } from "common/components/form/MultiSelect/MultiSelect";
import { Select } from "common/components/form/Select";
import { useTranslate } from "common/utilities/useTranslate";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import * as Styled from "domains/moderate/sources/components/FilterSourceListModal/FilterSourceListModal.styled";
import {
  filterSourcesFormSchema,
  type FilterSourcesFormState,
} from "domains/moderate/sources/components/FilterSourceListModal/types";
import {
  CONTENT_TYPE_FILTER_OPTIONS,
  COUNTRY_OPTIONS,
  DEFAULT_CONTENT_TYPE_OPTION,
  DEFAULT_LICENSE_OPTION,
  DEFAULT_PUBLICATION_TYPE_OPTION,
  DEFAULT_SOURCE_TYPE_OPTION,
  DEFAULT_STATUS_SELECT_OPTION,
  GREATER_REGION_OPTIONS,
  LICENSE_FILTER_OPTIONS,
  PUBLICATION_TYPE_FILTER_OPTIONS,
  REGION_OPTIONS,
  SOURCE_TYPE_FILTER_OPTIONS,
  STATUS_SELECT_FILTER_OPTIONS,
} from "domains/moderate/sources/constants";
import { getSourceFilters } from "domains/moderate/sources/selectors";
import { sourceFilterSlice } from "domains/moderate/sources/sources.slice";

export const FilterSourceListModal = () => {
  const dispatch = useDispatch();
  const translate = useTranslate();

  const activeSourceFilters = useSelector(getSourceFilters);

  const { control, handleSubmit, reset } = useForm({
    resolver: zodResolver(filterSourcesFormSchema),
    defaultValues: {
      status: STATUS_SELECT_FILTER_OPTIONS.find((option) => option.value === activeSourceFilters.status),
      type: SOURCE_TYPE_FILTER_OPTIONS.find((option) => option.value === activeSourceFilters.type),
      publicationType: PUBLICATION_TYPE_FILTER_OPTIONS.find(
        (option) => option.value === activeSourceFilters.publicationType,
      ),
      contentType: CONTENT_TYPE_FILTER_OPTIONS.find((option) => option.value === activeSourceFilters.contentType),
      license: LICENSE_FILTER_OPTIONS.find((option) => option.value === activeSourceFilters.license),
      countries: COUNTRY_OPTIONS.filter((option) => activeSourceFilters.countries.includes(option.value)),
      regions: REGION_OPTIONS.filter((option) => activeSourceFilters.regions.includes(option.value)),
      greaterRegions: GREATER_REGION_OPTIONS.filter((option) =>
        activeSourceFilters.greaterRegions?.includes(option.value),
      ),
    },
  });

  const handleResetClick = (e: MouseEvent) => {
    e.preventDefault();
    const initialFilterState = sourceFilterSlice.getInitialState();

    dispatch(sourceFilterSlice.actions.updateSourceFilters(initialFilterState));
    reset({
      status: DEFAULT_STATUS_SELECT_OPTION,
      type: DEFAULT_SOURCE_TYPE_OPTION,
      publicationType: DEFAULT_PUBLICATION_TYPE_OPTION,
      contentType: DEFAULT_CONTENT_TYPE_OPTION,
      license: DEFAULT_LICENSE_OPTION,
      countries: [],
      regions: [],
      greaterRegions: [],
    });
  };

  const onSubmit: SubmitHandler<FilterSourcesFormState> = (data) => {
    dispatch(
      sourceFilterSlice.actions.updateSourceFilters({
        status: data.status?.value,
        type: data.type?.value,
        publicationType: data.publicationType?.value,
        contentType: data.contentType?.value,
        license: data.license?.value,
        countries: data.countries?.map((country) => country.value) ?? [],
        regions: data.regions?.map((region) => region.value) ?? [],
        greaterRegions: data.greaterRegions?.map((greaterRegion) => greaterRegion.value) ?? [],
      }),
    );
  };

  return (
    <div>
      <ModalHeader title={translate("app.moderate.sources.filter.title")} />
      <Styled.FilterSourcesForm onSubmit={handleSubmit(onSubmit)}>
        <Styled.FormFieldsContainer>
          <label htmlFor="status-select">{translate("app.moderate.sources.status.label")}</label>
          <Controller
            control={control}
            name="status"
            render={({ field: { value, onChange } }) => (
              <Select
                accessibilityId="status-select"
                value={value}
                options={STATUS_SELECT_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            )}
          />
          <label htmlFor="type-select">{translate("app.moderate.sources.type.label")}</label>
          <Controller
            control={control}
            name="type"
            render={({ field: { value, onChange } }) => (
              <Select
                accessibilityId="type-select"
                value={value}
                options={SOURCE_TYPE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            )}
          />
          <label htmlFor="publication-type-select">{translate("app.moderate.sources.publicationType.label")}</label>
          <Controller
            control={control}
            name="publicationType"
            render={({ field: { value, onChange } }) => (
              <Select
                accessibilityId="publication-type-select"
                value={value}
                options={PUBLICATION_TYPE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            )}
          />
          <label htmlFor="content-type-select">{translate("app.moderate.sources.contentType.label")}</label>
          <Controller
            control={control}
            name="contentType"
            render={({ field: { value, onChange } }) => (
              <Select
                accessibilityId="content-type-select"
                value={value}
                options={CONTENT_TYPE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            )}
          />
          <label htmlFor="license-select">{translate("app.moderate.sources.license.label")}</label>
          <Controller
            control={control}
            name="license"
            render={({ field: { value, onChange } }) => (
              <Select
                accessibilityId="license-select"
                value={value}
                options={LICENSE_FILTER_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => onChange(option.value === value?.value ? undefined : option),
                }))}
              />
            )}
          />
          <label htmlFor="country-multi-select">{translate("app.moderate.sources.country.label")}</label>
          <Controller
            control={control}
            name="countries"
            render={({ field: { value, onChange } }) => (
              <MultiSelect
                accessibilityId="country-multi-select"
                placeholder="Select countries..."
                values={value}
                options={COUNTRY_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => {
                    if (value?.find((val) => val.value === option.value))
                      onChange(value.filter((val) => val.value !== option.value));
                    else onChange([...(value || []), option]);
                  },
                }))}
                removeOption={(option) => onChange(value?.filter((val) => val.value !== option.value))}
              />
            )}
          />
          <label htmlFor="regions-multi-select">{translate("app.moderate.sources.region.label")}</label>
          <Controller
            control={control}
            name="regions"
            render={({ field: { value, onChange } }) => (
              <MultiSelect
                accessibilityId="regions-multi-select"
                placeholder="Select regions..."
                values={value}
                options={REGION_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => {
                    if (value?.find((val) => val.value === option.value))
                      onChange(value.filter((val) => val.value !== option.value));
                    else onChange([...(value || []), option]);
                  },
                }))}
                removeOption={(option) => onChange(value?.filter((val) => val.value !== option.value))}
              />
            )}
          />
          <label htmlFor="greater-regions-multi-select">{translate("app.moderate.sources.greaterRegion.label")}</label>
          <Controller
            control={control}
            name="greaterRegions"
            render={({ field: { value, onChange } }) => (
              <MultiSelect
                accessibilityId="greater-regions-multi-select"
                placeholder="Select greater regions..."
                values={value}
                options={GREATER_REGION_OPTIONS.map((option) => ({
                  ...option,
                  onClick: () => {
                    if (value?.find((val) => val.value === option.value))
                      onChange(value.filter((val) => val.value !== option.value));
                    else onChange([...(value || []), option]);
                  },
                }))}
                removeOption={(option) => onChange(value?.filter((val) => val.value !== option.value))}
              />
            )}
          />
        </Styled.FormFieldsContainer>
        <Styled.FormButtonsContainer>
          <UIButton fullwidth type="submit">
            {translate("app.moderate.sources.filter.button.apply")}
          </UIButton>
          <UIButton fullwidth variant="neutral" as="button" onClick={handleResetClick}>
            {translate("app.moderate.sources.filter.button.clear")}
          </UIButton>
        </Styled.FormButtonsContainer>
      </Styled.FilterSourcesForm>
    </div>
  );
};
